import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AuthenticatedHeader.css';
import { Sun, Moon, LogOut } from 'react-feather';
import { useTheme } from '../ThemeContext';
import { authService, subscriptionService } from '../services/api';

// Import avatar SVGs
import { ReactComponent as LOTRAvatar } from '../assets/avatars/lotr.svg';
import { ReactComponent as GOTAvatar } from '../assets/avatars/got.svg';
import { ReactComponent as CyberpunkAvatar } from '../assets/avatars/cyberpunk.svg';
import { ReactComponent as GTAAvatar } from '../assets/avatars/gta.svg';
import { ReactComponent as HackerAvatar } from '../assets/avatars/hacker.svg';
import { ReactComponent as StarWarsAvatar } from '../assets/avatars/starwars.svg';
import { ReactComponent as MarvelAvatar } from '../assets/avatars/marvel.svg';
import { ReactComponent as AnimeAvatar } from '../assets/avatars/anime.svg';
import { ReactComponent as PixelAvatar } from '../assets/avatars/pixel.svg';
import { ReactComponent as DisneyAvatar } from '../assets/avatars/disney.svg';

const AuthenticatedHeader = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [userAvatar, setUserAvatar] = useState('lotr');
  const [isPro, setIsPro] = useState(false);
  const { theme, toggleTheme } = useTheme();
  
  // Placeholder notification count - should be fetched from API in production
  const notificationCount = 356;
  
  useEffect(() => {
    // Load user avatar from localStorage
    const savedAvatar = localStorage.getItem('userAvatar') || 'lotr';
    setUserAvatar(savedAvatar);
    
    // Fetch subscription status
    const fetchSubscriptionStatus = async () => {
      try {
        const subStatus = await subscriptionService.getStatus();
        setIsPro(subStatus.tier === 'pro');
      } catch (_) {
        // Fail silently – show no badge
        setIsPro(false);
      }
    };
    
    fetchSubscriptionStatus();
    
    // Listen for avatar updates
    const handleAvatarUpdate = (event) => {
      setUserAvatar(event.detail.avatar);
    };
    
    // Listen for subscription updates
    const handleSubscriptionUpdate = (e) => {
      if (e.detail?.tier === 'pro') {
        setIsPro(true);
      } else {
        setIsPro(false);
      }
    };
    
    document.addEventListener('avatar-updated', handleAvatarUpdate);
    window.addEventListener('subscription-updated', handleSubscriptionUpdate);
    
    return () => {
      document.removeEventListener('avatar-updated', handleAvatarUpdate);
      window.removeEventListener('subscription-updated', handleSubscriptionUpdate);
    };
  }, []);
  
  // Map of avatar components
  const avatarComponents = {
    lotr: LOTRAvatar,
    got: GOTAvatar,
    cyberpunk: CyberpunkAvatar,
    gta: GTAAvatar,
    hacker: HackerAvatar,
    starwars: StarWarsAvatar,
    marvel: MarvelAvatar,
    anime: AnimeAvatar,
    pixel: PixelAvatar,
    disney: DisneyAvatar
  };
  
  // Get the current avatar component
  const AvatarComponent = avatarComponents[userAvatar] || LOTRAvatar;
  
  // Handle logout
  const handleLogoutClick = () => {
    if (onLogout) {
      // Use the provided onLogout function
      onLogout();
    } else {
      // Fallback to default logout behavior
      authService.logout();
      navigate('/login');
    }
  };
  
  return (
    <header className={`authenticated-header ${theme}`}>
      <div className="header-content">
        {/* <div className="search-container">
          <div className="search-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>
          <input 
            type="text" 
            placeholder="Search SecondBrainAI" 
            className="search-input" 
          />
        </div> */}
        
        <div className="header-actions">
          
          {/* Get Pro Button - Only show for non-pro users */}
          {!isPro && (
            <button 
              className={`get-pro-button ${theme}`}
              onClick={() => navigate('/settings')}
            >
              Get Pro
            </button>
          )}
          
          {/* Theme Toggle Switch */}
          <div className={`theme-toggle-switch ${theme}`} onClick={toggleTheme}>
            <div className="toggle-track">
              <div className="toggle-thumb">
                <Sun className={`icon icon-sun ${theme === 'dark' ? 'active' : ''}`} size={16} />
                <Moon className={`icon icon-moon ${theme === 'light' ? 'active' : ''}`} size={16} />
              </div>
            </div>
          </div>
          
          <div className="profile-container">
            <div 
              className="profile-icon" 
              onClick={() => navigate('/settings')}
            >
              {userAvatar ? (
                <div className="user-avatar-container">
                  <AvatarComponent />
                </div>
              ) : (
                <div className="profile-avatar">
                  {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default AuthenticatedHeader; 